/*  Retainer product page — Invisique Infinite.
    ────────────────────────────────────────────────────────────────
    iter100.146 — Rewritten as a thin caller over the shared
    `product-page/sections.jsx` renderers so the Retainer page now
    matches Secure / Serene / Apex visually and structurally. Every
    hero/section string is CMS-driven under page slug `product_infinite`
    (Admin → Landing Page → Content → Retainer).

    Naming rule:
      • Sub-menu / navigation label stays generic: "Retainer".
      • On this PAGE the brand identity "Invisique Infinite" is used.
*/
import React, { useEffect } from "react";
import { useProductPageCms } from "@/lib/useProductPageCms";
import {
    Hero, TrustBar, PainPoint, Benefits, Engineering, Journey,
    ComparisonTable, Testimonials, DoctorCredibility, Faq,
    RelatedProducts, FinalCta,
} from "@/components/product-page/sections";
import GuidedCallbackForm from "@/components/site/GuidedCallbackForm";
import SeoHead from "@/components/site/SeoHead";


const BRAND = "Invisique Infinite";
const CMS_PAGE = "product_infinite";
const TEST_PREFIX = "infinite";
const PRODUCT_SLUG = "retainer";


/* Hero image — code-side fallback. CMS `hero.image_url` overrides. */
const HERO_GALLERY = [
    {
        label: "Product",
        url: "/marketing/invisique-infinite-retainers.png",
        alt: "Invisique's Infinite Retainers on a 3D dental scan",
    },
];


/* Comparison table (Section 8). Cell values encode tone:
   yes / warn / no / na, optional custom label after a hyphen. */
const COMPARISON_HEADERS = [
    "Feature", BRAND, "Standard Vacuum-Formed", "Budget Smile Kits", "Original Aligners",
];
const COMPARISON_ROWS = [
    ["Material Quality",    "yes-Multi-layer polymer",  "warn-Single-layer plastic", "no-Generic plastic",    "warn-Movement-grade"],
    ["Durability",          "yes-2–3 years / set",       "warn-6–12 months",          "no-3–6 months",         "no-Weeks"],
    ["Stain Resistance",    "yes-High",                  "warn-Yellows",              "no-Discolours fast",    "no-Becomes opaque"],
    ["Fit Precision",       "yes-Laser-cut to gumline", "warn-Generic",              "no-One-size-fits-most", "warn-Loose over time"],
    ["Lifetime Scan Archive","yes-Patient-owned",         "no-Lost after treatment",   "na",                    "na"],
    ["Doctor Support",      "yes-Annual refit included", "warn-Extra cost",           "no",                    "no"],
    ["Lifetime Access",     "yes-Move anywhere",         "warn-Clinic-dependent",     "no",                    "no"],
    ["Replacement Cost",    "yes-Reorder from portal",   "warn-Requires new scan",    "no-Low quality",        "na"],
];


const RELATED = [
    { title: "Invisique Secure",  subtitle: "Sleep protected",       body: "Custom night guard for teeth grinding.",   href: "/mouthguards-and-retainer/night-guard",  icon: "Moon",    tint: "from-indigo-100 to-slate-100", iconTint: "text-indigo-600" },
    { title: "Invisique Serene",  subtitle: "Quiet nights",          body: "For peaceful, uninterrupted sleep.",       href: "/mouthguards-and-retainer/anti-snoring", icon: "Bed",     tint: "from-sky-100 to-cyan-100",     iconTint: "text-sky-600" },
    { title: "Invisique Apex",    subtitle: "Play hard, protect harder", body: "For athletes who need protection.",  href: "/mouthguards-and-retainer/sports-guard", icon: "Barbell", tint: "from-amber-100 to-rose-100",   iconTint: "text-amber-600" },
];


export default function InvisiqueInfinite() {
    const { t, arr, loading } = useProductPageCms(CMS_PAGE);

    useEffect(() => {
        // SeoHead below drives document.title from CMS.
    }, []);

    const scrollToLeadForm = () => {
        document.getElementById("guided-callback-anchor")
            ?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    if (loading) {
        return <div className="min-h-[60vh] bg-white" aria-hidden="true" data-testid="infinite-loading" />;
    }

    return (
        <div className="bg-white" data-testid="invisique-infinite-page">
            <SeoHead
                page={CMS_PAGE}
                fallbackTitle={`${BRAND} · Retainers for Life · Invisique`}
                fallbackDescription="Multi-layer polymer retainers, 30% stronger. Lifetime scan archive."
            />
            <Hero
                t={t}
                arr={arr}
                testPrefix={TEST_PREFIX}
                gallery={HERO_GALLERY}
                halosTint="emerald"
                captionIcon="Diamond"
                credibilityIcon="Star"
            />
            <TrustBar t={t} arr={arr} testPrefix={TEST_PREFIX} />
            <PainPoint t={t} arr={arr} testPrefix={TEST_PREFIX} layout="rows" />
            <Benefits t={t} arr={arr} testPrefix={TEST_PREFIX} />
            <Engineering t={t} arr={arr} testPrefix={TEST_PREFIX} />
            <Journey t={t} arr={arr} testPrefix={TEST_PREFIX} bg="bg-secondary/30" />
            <ComparisonTable
                t={t}
                arr={arr}
                testPrefix={TEST_PREFIX}
                headers={COMPARISON_HEADERS}
                rows={COMPARISON_ROWS}
            />
            <Testimonials t={t} arr={arr} testPrefix={TEST_PREFIX} bg="bg-white" scope="product_infinite" />
            <DoctorCredibility t={t} arr={arr} testPrefix={TEST_PREFIX} gradient="from-primary/[0.06] via-white to-emerald-50" />
            <Faq t={t} arr={arr} testPrefix={TEST_PREFIX} />
            <GuidedCallbackForm
                productSlug={PRODUCT_SLUG}
                defaultInterest={PRODUCT_SLUG}
                themeTint="indigo"
                testPrefix={TEST_PREFIX}
                headline="Ready to Protect Your Smile Forever?"
                blurb="Leave your details and an Invisique specialist will reach out to guide you to the nearest certified retention doctor."
                extraSelects={[
                    {
                        key: "wear_preference",
                        label: "Your preferred wear",
                        options: ["Night only", "Full-time", "Not sure — my doctor will advise"],
                    },
                ]}
            />
            <RelatedProducts
                t={t}
                arr={arr}
                testPrefix={TEST_PREFIX}
                items={RELATED}
            />
            <FinalCta
                t={t}
                testPrefix={TEST_PREFIX}
                onCallbackClick={scrollToLeadForm}
            />
        </div>
    );
}
